// @ts-nocheck — productions tables not yet in generated supabase-types; remove after next type regeneration
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  GripVertical,
  AlertTriangle,
  Send,
  CheckCircle2,
  Circle,
  Clock,
  MoreHorizontal,
  UserPlus,
  CalendarDays,
  Link2,
  Pencil,
  Check,
  Flag,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────────────────────

type ProductionPhase = 'development' | 'pre_production' | 'production' | 'post_production' | 'completed' | 'on_hold';
type PipelineTemplate = 'animation' | 'live_action';
type ProductionType = 'film' | 'series' | 'animation' | 'short' | 'pilot' | 'documentary' | 'commercial' | 'other';
type TaskStatus = 'backlog' | 'todo' | 'in_progress' | 'review' | 'done';
type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
type ShotStatus = 'not_started' | 'in_progress' | 'blocked' | 'approved';
type PipelineStage =
  | 'storyboard' | 'layout' | 'animation' | 'render' | 'comp'
  | 'prep' | 'shoot' | 'edit' | 'grade' | 'final';

interface Production {
  id: string;
  publisher_id: string;
  work_id: string | null;
  title: string;
  description: string | null;
  production_type: ProductionType;
  pipeline_template: PipelineTemplate;
  phase: ProductionPhase;
  start_date: string | null;
  target_release_date: string | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

interface ProductionMember {
  id: string;
  production_id: string;
  auth_user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  department: string | null;
  role_label: string | null;
  is_admin: boolean;
}

interface Task {
  id: string;
  production_id: string;
  shot_id: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  department: string | null;
  assignee_id: string | null;
  due_date: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface Shot {
  id: string;
  production_id: string;
  sequence_code: string;
  shot_code: string;
  description: string | null;
  pipeline_stage: PipelineStage;
  status: ShotStatus;
  assignee_id: string | null;
  frame_count: number | null;
  thumbnail_url: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface Milestone {
  id: string;
  production_id: string;
  title: string;
  due_date: string | null;
  completed: boolean;
  completed_at: string | null;
  sort_order: number;
  created_at: string;
}

interface TaskComment {
  id: string;
  task_id: string;
  author_id: string;
  body: string;
  created_at: string;
}

interface StudioMember {
  auth_user_id: string;
  full_name: string | null;
  email: string | null;
}

interface GuildProfile {
  user_id: string;
  display_name: string | null;
  headline: string | null;
  avatar_url: string | null;
}

// ─── Pipeline stage configs ─────────────────────────────────────────────────────

const ANIMATION_STAGES: PipelineStage[] = ['storyboard', 'layout', 'animation', 'render', 'comp', 'final'];
const LIVE_ACTION_STAGES: PipelineStage[] = ['prep', 'shoot', 'edit', 'grade', 'final'];

function stagesForTemplate(t: PipelineTemplate): PipelineStage[] {
  return t === 'animation' ? ANIMATION_STAGES : LIVE_ACTION_STAGES;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
}

function fmtDate(d: string | null | undefined) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(d: string) {
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const PHASE_OPTIONS: { value: ProductionPhase; label: string }[] = [
  { value: 'development', label: 'Development' },
  { value: 'pre_production', label: 'Pre-Production' },
  { value: 'production', label: 'Production' },
  { value: 'post_production', label: 'Post-Production' },
  { value: 'completed', label: 'Completed' },
  { value: 'on_hold', label: 'On Hold' },
];

const PRIORITY_META: Record<TaskPriority, { label: string; color: string }> = {
  low:    { label: 'Low',    color: 'text-muted-foreground' },
  medium: { label: 'Medium', color: 'text-blue-400' },
  high:   { label: 'High',   color: 'text-yellow-400' },
  urgent: { label: 'Urgent', color: 'text-red-400' },
};

const STATUS_COLUMNS: { id: TaskStatus; label: string }[] = [
  { id: 'backlog',     label: 'Backlog' },
  { id: 'todo',        label: 'To Do' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'review',      label: 'Review' },
  { id: 'done',        label: 'Done' },
];

const SHOT_STATUS_META: Record<ShotStatus, { label: string; color: string }> = {
  not_started: { label: 'Not Started', color: 'text-muted-foreground' },
  in_progress: { label: 'In Progress', color: 'text-blue-400' },
  blocked:     { label: 'Blocked',     color: 'text-red-400' },
  approved:    { label: 'Approved',    color: 'text-green-400' },
};

/** Fractional midpoint between two sort orders */
function midpoint(a: number, b: number) { return (a + b) / 2; }

// ─── Priority Flag ──────────────────────────────────────────────────────────────

function PriorityFlag({ priority }: { priority: TaskPriority }) {
  const m = PRIORITY_META[priority];
  return <Flag className={`w-3 h-3 ${m.color}`} />;
}

// ─── Pipeline Stage Stepper ─────────────────────────────────────────────────────

function PipelineStepper({
  stages,
  current,
  onSelect,
}: {
  stages: PipelineStage[];
  current: PipelineStage;
  onSelect?: (s: PipelineStage) => void;
}) {
  const idx = stages.indexOf(current);
  return (
    <div className="flex items-center gap-0.5">
      {stages.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <button
            key={s}
            title={s}
            onClick={() => onSelect?.(s)}
            disabled={!onSelect}
            className={[
              'h-1.5 rounded-full transition-all',
              i < stages.length - 1 ? 'flex-1' : 'w-3',
              done  ? 'bg-primary/70' : '',
              active ? 'bg-primary' : '',
              !done && !active ? 'bg-border' : '',
              onSelect ? 'cursor-pointer hover:opacity-80' : 'cursor-default',
            ].join(' ')}
          />
        );
      })}
      <span className="ml-2 text-[10px] font-mono text-muted-foreground capitalize">{current}</span>
    </div>
  );
}

// ─── Task Card (Board) ──────────────────────────────────────────────────────────

function TaskCard({
  task,
  members,
  shots,
  onClick,
  dragging,
}: {
  task: Task;
  members: ProductionMember[];
  shots: Shot[];
  onClick: () => void;
  dragging?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const assignee = members.find(m => m.auth_user_id === task.assignee_id);
  const shot = shots.find(s => s.id === task.shot_id);
  const isOverdue = task.due_date && task.status !== 'done' && new Date(task.due_date) < new Date();

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div
        onClick={onClick}
        className={[
          'partner-card px-3 py-2.5 cursor-pointer select-none',
          dragging ? 'shadow-2xl scale-[1.02]' : '',
        ].join(' ')}
      >
        <div className="flex items-start gap-1.5">
          {/* Drag handle */}
          <button
            {...listeners}
            onClick={e => e.stopPropagation()}
            className="mt-0.5 text-muted-foreground/30 hover:text-muted-foreground cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-3 h-3" />
          </button>

          <div className="flex-1 min-w-0">
            {/* Priority + title */}
            <div className="flex items-start gap-1.5">
              <PriorityFlag priority={task.priority} />
              <span className="text-sm leading-snug flex-1">{task.title}</span>
            </div>

            {/* Meta chips */}
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {task.department && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-accent/50 text-muted-foreground">
                  {task.department}
                </span>
              )}
              {shot && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-primary/10 text-primary">
                  {shot.sequence_code}/{shot.shot_code}
                </span>
              )}
              {task.due_date && (
                <span className={`flex items-center gap-0.5 text-[9px] ${isOverdue ? 'text-red-400' : 'text-muted-foreground'}`}>
                  <Clock className="w-2 h-2" />
                  {fmtDate(task.due_date)}
                </span>
              )}
            </div>
          </div>

          {/* Assignee avatar */}
          {assignee && (
            <Avatar className="w-5 h-5 shrink-0 mt-0.5">
              <AvatarImage src={assignee.avatar_url ?? undefined} />
              <AvatarFallback className="text-[8px]">{initials(assignee.display_name)}</AvatarFallback>
            </Avatar>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Task Detail Sheet ──────────────────────────────────────────────────────────

function TaskSheet({
  task,
  members,
  shots,
  currentUserId,
  open,
  onClose,
  onSaved,
}: {
  task: Task | null;
  members: ProductionMember[];
  shots: Shot[];
  currentUserId: string;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: Task) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<TaskStatus>('backlog');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [department, setDepartment] = useState('');
  const [assigneeId, setAssigneeId] = useState('none');
  const [dueDate, setDueDate] = useState('');
  const [shotId, setShotId] = useState('none');
  const [saving, setSaving] = useState(false);

  const [comments, setComments] = useState<TaskComment[]>([]);
  const [commentBody, setCommentBody] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  useEffect(() => {
    if (!task) return;
    setTitle(task.title);
    setDescription(task.description ?? '');
    setStatus(task.status);
    setPriority(task.priority);
    setDepartment(task.department ?? '');
    setAssigneeId(task.assignee_id ?? 'none');
    setDueDate(task.due_date ?? '');
    setShotId(task.shot_id ?? 'none');
    setEditing(false);
    // Load comments
    supabase
      .from('production_task_comments')
      .select('*')
      .eq('task_id', task.id)
      .order('created_at')
      .then(({ data }) => setComments((data ?? []) as TaskComment[]));
  }, [task?.id]);

  if (!task) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('production_tasks')
        .update({
          title: title.trim(),
          description: description.trim() || null,
          status,
          priority,
          department: department.trim() || null,
          assignee_id: assigneeId === 'none' ? null : assigneeId,
          due_date: dueDate || null,
          shot_id: shotId === 'none' ? null : shotId,
        })
        .eq('id', task.id)
        .select()
        .single();
      if (error) throw error;
      onSaved(data as Task);
      setEditing(false);
      toast.success('Task updated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const postComment = async () => {
    if (!commentBody.trim()) return;
    setPostingComment(true);
    try {
      const { data, error } = await supabase
        .from('production_task_comments')
        .insert({ task_id: task.id, author_id: currentUserId, body: commentBody.trim() })
        .select()
        .single();
      if (error) throw error;
      setComments(prev => [...prev, data as TaskComment]);
      setCommentBody('');
    } catch (err: any) {
      toast.error('Failed to post comment');
    } finally {
      setPostingComment(false);
    }
  };

  const assignee = members.find(m => m.auth_user_id === task.assignee_id);

  return (
    <Sheet open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col p-0">
        <SheetHeader className="px-5 py-4 border-b border-border/50">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="font-display text-base truncate">
              {editing ? (
                <Input value={title} onChange={e => setTitle(e.target.value)} className="h-7 text-sm" autoFocus />
              ) : (
                task.title
              )}
            </SheetTitle>
            <div className="flex items-center gap-1.5 shrink-0">
              {editing ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  <Pencil className="w-3 h-3 mr-1" />Edit
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="p-5 space-y-5">
            {/* Fields grid */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Status</p>
                {editing ? (
                  <Select value={status} onValueChange={v => setStatus(v as TaskStatus)}>
                    <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_COLUMNS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="capitalize">{task.status.replace('_', ' ')}</span>
                )}
              </div>

              <div>
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Priority</p>
                {editing ? (
                  <Select value={priority} onValueChange={v => setPriority(v as TaskPriority)}>
                    <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(['low','medium','high','urgent'] as TaskPriority[]).map(p => (
                        <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className={`capitalize ${PRIORITY_META[task.priority].color}`}>{task.priority}</span>
                )}
              </div>

              <div>
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Assignee</p>
                {editing ? (
                  <Select value={assigneeId} onValueChange={setAssigneeId}>
                    <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {members.map(m => (
                        <SelectItem key={m.auth_user_id} value={m.auth_user_id}>
                          {m.display_name ?? m.auth_user_id.slice(0, 8)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : assignee ? (
                  <div className="flex items-center gap-1.5">
                    <Avatar className="w-4 h-4"><AvatarFallback className="text-[8px]">{initials(assignee.display_name)}</AvatarFallback></Avatar>
                    <span>{assignee.display_name}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">Unassigned</span>
                )}
              </div>

              <div>
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Due Date</p>
                {editing ? (
                  <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="h-7 text-xs" />
                ) : (
                  <span>{fmtDate(task.due_date)}</span>
                )}
              </div>

              <div>
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Department</p>
                {editing ? (
                  <Input value={department} onChange={e => setDepartment(e.target.value)} placeholder="e.g. Animation" className="h-7 text-xs" />
                ) : (
                  <span>{task.department ?? <span className="text-muted-foreground">—</span>}</span>
                )}
              </div>

              <div>
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Shot</p>
                {editing ? (
                  <Select value={shotId} onValueChange={setShotId}>
                    <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="No shot" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No shot</SelectItem>
                      {shots.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.sequence_code}/{s.shot_code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="font-mono text-xs text-primary">
                    {shots.find(s => s.id === task.shot_id)
                      ? `${shots.find(s => s.id === task.shot_id)!.sequence_code}/${shots.find(s => s.id === task.shot_id)!.shot_code}`
                      : <span className="text-muted-foreground font-sans">—</span>}
                  </span>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Description</p>
              {editing ? (
                <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} className="resize-none text-sm" />
              ) : (
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {task.description || <span className="italic">No description</span>}
                </p>
              )}
            </div>

            <Separator />

            {/* Comments */}
            <div>
              <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-3">Comments</p>
              <div className="space-y-3 mb-3">
                {comments.length === 0 && (
                  <p className="text-xs text-muted-foreground italic">No comments yet.</p>
                )}
                {comments.map(c => (
                  <div key={c.id} className="text-sm">
                    <p className="text-[10px] text-muted-foreground mb-0.5">{fmtDateTime(c.created_at)}</p>
                    <p className="leading-relaxed whitespace-pre-wrap">{c.body}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Textarea
                  value={commentBody}
                  onChange={e => setCommentBody(e.target.value)}
                  placeholder="Add a comment…"
                  rows={2}
                  className="resize-none text-sm flex-1"
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postComment(); }}
                />
                <Button size="icon" variant="outline" onClick={postComment} disabled={postingComment || !commentBody.trim()}>
                  <Send className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

// ─── Board Tab ──────────────────────────────────────────────────────────────────

function BoardTab({
  productionId,
  members,
  shots,
  isManager,
  currentUserId,
}: {
  productionId: string;
  members: ProductionMember[];
  shots: Shot[];
  isManager: boolean;
  currentUserId: string;
}) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [addingInColumn, setAddingInColumn] = useState<TaskStatus | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('production_tasks')
      .select('*')
      .eq('production_id', productionId)
      .order('sort_order');
    if (error) { toast.error('Failed to load tasks'); return; }
    setTasks((data ?? []) as Task[]);
    setLoading(false);
  }, [productionId]);

  useEffect(() => { load(); }, [load]);

  const columnTasks = (status: TaskStatus) =>
    tasks.filter(t => t.status === status).sort((a, b) => a.sort_order - b.sort_order);

  const handleDragStart = (e: DragStartEvent) => {
    setActiveId(String(e.active.id));
  };

  const handleDragOver = (e: DragOverEvent) => {
    const { active, over } = e;
    if (!over) return;
    // Determine if over a column label (data-column attribute) or a task card
    const overId = String(over.id);
    const overTask = tasks.find(t => t.id === overId);
    const overStatus = overTask?.status ?? (over.data.current?.column as TaskStatus | undefined);
    if (!overStatus) return;
    const activeTask = tasks.find(t => t.id === String(active.id));
    if (!activeTask || activeTask.status === overStatus) return;
    setTasks(prev => prev.map(t => t.id === activeTask.id ? { ...t, status: overStatus } : t));
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;

    const activeTask = tasks.find(t => t.id === String(active.id));
    if (!activeTask) return;

    const overId = String(over.id);
    const overTask = tasks.find(t => t.id === overId);
    const targetStatus = overTask?.status ?? activeTask.status;
    const col = tasks.filter(t => t.status === targetStatus && t.id !== activeTask.id).sort((a, b) => a.sort_order - b.sort_order);

    let newSortOrder: number;
    if (!overTask || overTask.id === activeTask.id) {
      // Dropped on empty column or same position
      newSortOrder = col.length > 0 ? col[col.length - 1].sort_order + 1000 : 1000;
    } else {
      const overIdx = col.findIndex(t => t.id === overTask.id);
      const prev = col[overIdx - 1];
      const next = col[overIdx];
      if (!prev) newSortOrder = (next?.sort_order ?? 1000) / 2;
      else if (!next) newSortOrder = prev.sort_order + 1000;
      else newSortOrder = midpoint(prev.sort_order, next.sort_order);
    }

    // Optimistic update
    setTasks(prev => prev.map(t => t.id === activeTask.id ? { ...t, status: targetStatus, sort_order: newSortOrder } : t));

    await supabase
      .from('production_tasks')
      .update({ status: targetStatus, sort_order: newSortOrder })
      .eq('id', activeTask.id);
  };

  const addTask = async (status: TaskStatus) => {
    if (!newTaskTitle.trim()) return;
    const col = columnTasks(status);
    const sort_order = col.length > 0 ? col[col.length - 1].sort_order + 1000 : 1000;
    const { data, error } = await supabase
      .from('production_tasks')
      .insert({
        production_id: productionId,
        title: newTaskTitle.trim(),
        status,
        priority: 'medium',
        sort_order,
        created_by: currentUserId,
      })
      .select()
      .single();
    if (error) { toast.error('Failed to create task'); return; }
    setTasks(prev => [...prev, data as Task]);
    setNewTaskTitle('');
    setAddingInColumn(null);
  };

  const activeTask = activeId ? tasks.find(t => t.id === activeId) : null;

  if (loading) return <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">Loading board…</div>;

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        {/* Horizontal scroll board */}
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3" style={{ minWidth: `${STATUS_COLUMNS.length * 264}px` }}>
            {STATUS_COLUMNS.map(col => {
              const colTasks = columnTasks(col.id);
              return (
                <div key={col.id} className="flex-none w-64 flex flex-col gap-2">
                  {/* Column header */}
                  <div className="flex items-center justify-between px-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{col.label}</span>
                    <span className="text-[10px] text-muted-foreground/50">{colTasks.length}</span>
                  </div>

                  {/* Cards */}
                  <div
                    className="flex-1 min-h-[120px] rounded-lg bg-muted/20 p-2 space-y-2 border border-dashed border-border/30"
                    data-column={col.id}
                  >
                    <SortableContext items={colTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                      {colTasks.map(t => (
                        <TaskCard
                          key={t.id}
                          task={t}
                          members={members}
                          shots={shots}
                          onClick={() => setSelectedTask(t)}
                        />
                      ))}
                    </SortableContext>

                    {/* Add task inline */}
                    {addingInColumn === col.id ? (
                      <div className="partner-card p-2 space-y-1.5">
                        <Input
                          autoFocus
                          value={newTaskTitle}
                          onChange={e => setNewTaskTitle(e.target.value)}
                          placeholder="Task title…"
                          className="h-7 text-xs"
                          onKeyDown={e => {
                            if (e.key === 'Enter') addTask(col.id);
                            if (e.key === 'Escape') { setAddingInColumn(null); setNewTaskTitle(''); }
                          }}
                        />
                        <div className="flex gap-1">
                          <Button size="sm" className="h-6 text-xs px-2" onClick={() => addTask(col.id)}>Add</Button>
                          <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => { setAddingInColumn(null); setNewTaskTitle(''); }}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setAddingInColumn(col.id)}
                        className="w-full text-left text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors px-1 py-0.5 flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Add task
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <DragOverlay>
          {activeTask ? (
            <div className="partner-card px-3 py-2.5 shadow-2xl scale-[1.02] cursor-grabbing">
              <div className="flex items-center gap-1.5">
                <PriorityFlag priority={activeTask.priority} />
                <span className="text-sm">{activeTask.title}</span>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <TaskSheet
        task={selectedTask}
        members={members}
        shots={shots}
        currentUserId={currentUserId}
        open={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        onSaved={updated => {
          setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
          setSelectedTask(updated);
        }}
      />
    </>
  );
}

// ─── Shots Tab ──────────────────────────────────────────────────────────────────

function ShotsTab({
  productionId,
  pipelineTemplate,
  members,
  isManager,
}: {
  productionId: string;
  pipelineTemplate: PipelineTemplate;
  members: ProductionMember[];
  isManager: boolean;
}) {
  const [shots, setShots] = useState<Shot[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  // New shot form
  const [seqCode, setSeqCode] = useState('');
  const [shotCode, setShotCode] = useState('');
  const [description, setDescription] = useState('');
  const [frameCount, setFrameCount] = useState('');
  const [pipelineStage, setPipelineStage] = useState<PipelineStage>('storyboard');
  const [assigneeId, setAssigneeId] = useState('none');
  const [saving, setSaving] = useState(false);

  const stages = stagesForTemplate(pipelineTemplate);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('production_shots')
      .select('*')
      .eq('production_id', productionId)
      .order('sort_order');
    if (error) { toast.error('Failed to load shots'); return; }
    setShots((data ?? []) as Shot[]);
    setLoading(false);
  }, [productionId]);

  useEffect(() => { load(); }, [load]);

  const createShot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!seqCode.trim() || !shotCode.trim()) { toast.error('Sequence and shot codes required'); return; }
    setSaving(true);
    const sort_order = shots.length > 0 ? shots[shots.length - 1].sort_order + 1000 : 1000;
    const { data, error } = await supabase
      .from('production_shots')
      .insert({
        production_id: productionId,
        sequence_code: seqCode.trim().toUpperCase(),
        shot_code: shotCode.trim().toUpperCase(),
        description: description.trim() || null,
        pipeline_stage: pipelineStage,
        status: 'not_started',
        frame_count: frameCount ? parseInt(frameCount) : null,
        assignee_id: assigneeId === 'none' ? null : assigneeId,
        sort_order,
      })
      .select()
      .single();
    setSaving(false);
    if (error) { toast.error('Failed to create shot'); return; }
    setShots(prev => [...prev, data as Shot]);
    setAddOpen(false);
    setSeqCode(''); setShotCode(''); setDescription(''); setFrameCount(''); setAssigneeId('none');
    setPipelineStage('storyboard');
  };

  const updateShotStage = async (shot: Shot, stage: PipelineStage) => {
    setShots(prev => prev.map(s => s.id === shot.id ? { ...s, pipeline_stage: stage } : s));
    await supabase.from('production_shots').update({ pipeline_stage: stage }).eq('id', shot.id);
  };

  const updateShotStatus = async (shot: Shot, status: ShotStatus) => {
    setShots(prev => prev.map(s => s.id === shot.id ? { ...s, status } : s));
    await supabase.from('production_shots').update({ status }).eq('id', shot.id);
  };

  // Group by sequence
  const sequences = [...new Set(shots.map(s => s.sequence_code))].sort();

  if (loading) return <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">Loading shots…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{shots.length} shot{shots.length !== 1 ? 's' : ''}</span>
        {isManager && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add Shot
          </Button>
        )}
      </div>

      {shots.length === 0 ? (
        <div className="partner-card flex flex-col items-center justify-center py-12 text-center">
          <p className="text-muted-foreground text-sm">No shots yet.</p>
          {isManager && <Button size="sm" className="mt-3" onClick={() => setAddOpen(true)}>Add first shot</Button>}
        </div>
      ) : (
        <div className="space-y-6">
          {sequences.map(seq => {
            const seqShots = shots.filter(s => s.sequence_code === seq);
            return (
              <div key={seq}>
                <h3 className="kinora-eyebrow mb-2">{seq}</h3>
                <div className="partner-card overflow-hidden divide-y divide-border/40">
                  {/* Table header */}
                  <div className="grid grid-cols-[auto_1fr_200px_90px_80px_80px] gap-3 px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    <span>Shot</span>
                    <span>Description</span>
                    <span>Pipeline Stage</span>
                    <span>Status</span>
                    <span>Frames</span>
                    <span>Assignee</span>
                  </div>
                  {seqShots.map(shot => {
                    const assignee = members.find(m => m.auth_user_id === shot.assignee_id);
                    const sm = SHOT_STATUS_META[shot.status];
                    return (
                      <div
                        key={shot.id}
                        className={[
                          'grid grid-cols-[auto_1fr_200px_90px_80px_80px] gap-3 px-4 py-2.5 items-center text-sm',
                          shot.status === 'blocked' ? 'bg-red-500/5' : '',
                        ].join(' ')}
                      >
                        <span className="font-mono text-xs text-primary whitespace-nowrap">{shot.sequence_code}/{shot.shot_code}</span>
                        <span className="text-xs text-muted-foreground truncate">{shot.description ?? '—'}</span>
                        <div className="w-full">
                          <PipelineStepper
                            stages={stages}
                            current={shot.pipeline_stage}
                            onSelect={isManager ? s => updateShotStage(shot, s) : undefined}
                          />
                        </div>
                        <div>
                          {isManager ? (
                            <Select value={shot.status} onValueChange={v => updateShotStatus(shot, v as ShotStatus)}>
                              <SelectTrigger className="h-6 text-[10px] border-0 bg-transparent px-0">
                                <span className={sm.color}>{sm.label}</span>
                              </SelectTrigger>
                              <SelectContent>
                                {(['not_started','in_progress','blocked','approved'] as ShotStatus[]).map(s => (
                                  <SelectItem key={s} value={s}>{SHOT_STATUS_META[s].label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={`text-xs ${sm.color}`}>
                              {shot.status === 'blocked' && <AlertTriangle className="inline w-3 h-3 mr-0.5" />}
                              {sm.label}
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-mono text-muted-foreground">{shot.frame_count ?? '—'}</span>
                        <div>
                          {assignee ? (
                            <Avatar className="w-5 h-5">
                              <AvatarImage src={assignee.avatar_url ?? undefined} />
                              <AvatarFallback className="text-[8px]">{initials(assignee.display_name)}</AvatarFallback>
                            </Avatar>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add shot dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Add Shot</DialogTitle></DialogHeader>
          <form onSubmit={createShot} className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Sequence Code *</Label>
                <Input value={seqCode} onChange={e => setSeqCode(e.target.value)} placeholder="SQ010" className="font-mono uppercase" />
              </div>
              <div className="space-y-1">
                <Label>Shot Code *</Label>
                <Input value={shotCode} onChange={e => setShotCode(e.target.value)} placeholder="0010" className="font-mono uppercase" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief shot description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Pipeline Stage</Label>
                <Select value={pipelineStage} onValueChange={v => setPipelineStage(v as PipelineStage)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {stages.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Frame Count</Label>
                <Input type="number" value={frameCount} onChange={e => setFrameCount(e.target.value)} placeholder="24" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Assignee</Label>
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {members.map(m => (
                    <SelectItem key={m.auth_user_id} value={m.auth_user_id}>
                      {m.display_name ?? m.auth_user_id.slice(0, 8)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Adding…' : 'Add Shot'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Milestones Tab ─────────────────────────────────────────────────────────────

function MilestonesTab({
  productionId,
  isManager,
}: {
  productionId: string;
  isManager: boolean;
}) {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('production_milestones')
      .select('*')
      .eq('production_id', productionId)
      .order('sort_order');
    if (error) { toast.error('Failed to load milestones'); return; }
    setMilestones((data ?? []) as Milestone[]);
    setLoading(false);
  }, [productionId]);

  useEffect(() => { load(); }, [load]);

  const addMilestone = async () => {
    if (!newTitle.trim()) return;
    const sort_order = milestones.length > 0 ? milestones[milestones.length - 1].sort_order + 1000 : 1000;
    const { data, error } = await supabase
      .from('production_milestones')
      .insert({ production_id: productionId, title: newTitle.trim(), due_date: newDate || null, completed: false, sort_order })
      .select().single();
    if (error) { toast.error('Failed to add milestone'); return; }
    setMilestones(prev => [...prev, data as Milestone]);
    setNewTitle(''); setNewDate(''); setAdding(false);
  };

  const toggleComplete = async (m: Milestone) => {
    if (!isManager) return;
    const completed = !m.completed;
    setMilestones(prev => prev.map(ms => ms.id === m.id ? { ...ms, completed, completed_at: completed ? new Date().toISOString() : null } : ms));
    await supabase.from('production_milestones').update({ completed, completed_at: completed ? new Date().toISOString() : null }).eq('id', m.id);
  };

  if (loading) return <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">Loading…</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      {milestones.length === 0 && !adding && (
        <div className="partner-card py-10 text-center">
          <p className="text-muted-foreground text-sm">No milestones yet.</p>
        </div>
      )}

      <div className="space-y-2">
        {milestones.map(m => (
          <div key={m.id} className={`partner-card flex items-center gap-3 px-4 py-3 ${m.completed ? 'opacity-60' : ''}`}>
            <button onClick={() => toggleComplete(m)} disabled={!isManager} className="text-primary">
              {m.completed
                ? <CheckCircle2 className="w-4 h-4 text-green-400" />
                : <Circle className="w-4 h-4" />
              }
            </button>
            <div className="flex-1 min-w-0">
              <p className={`text-sm ${m.completed ? 'line-through text-muted-foreground' : ''}`}>{m.title}</p>
              {m.due_date && (
                <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                  <CalendarDays className="w-2.5 h-2.5" />
                  {fmtDate(m.due_date)}
                </p>
              )}
            </div>
            {m.completed && m.completed_at && (
              <span className="text-[10px] text-muted-foreground">Completed {fmtDate(m.completed_at)}</span>
            )}
          </div>
        ))}
      </div>

      {isManager && (
        adding ? (
          <div className="partner-card p-4 space-y-3">
            <Input autoFocus value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Milestone title" onKeyDown={e => { if (e.key === 'Enter') addMilestone(); if (e.key === 'Escape') setAdding(false); }} />
            <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} />
            <div className="flex gap-2">
              <Button size="sm" onClick={addMilestone}>Add</Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add Milestone
          </Button>
        )
      )}
    </div>
  );
}

// ─── Team Tab ───────────────────────────────────────────────────────────────────

function TeamTab({
  productionId,
  publisherId,
  members,
  isManager,
  onMembersChange,
}: {
  productionId: string;
  publisherId: string;
  members: ProductionMember[];
  isManager: boolean;
  onMembersChange: (m: ProductionMember[]) => void;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [studioMembers, setStudioMembers] = useState<StudioMember[]>([]);
  const [guildProfiles, setGuildProfiles] = useState<GuildProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleLabel, setRoleLabel] = useState('');
  const [department, setDepartment] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!addOpen) return;
    supabase
      .from('publisher_users')
      .select('auth_user_id, full_name, email')
      .eq('publisher_id', publisherId)
      .eq('is_active', true)
      .then(({ data }) => setStudioMembers((data ?? []) as StudioMember[]));

    // Try guild_profiles (may be blocked by RLS)
    supabase
      .from('guild_profiles')
      .select('user_id, display_name, headline, avatar_url')
      .limit(30)
      .then(({ data }) => { if (data) setGuildProfiles(data as GuildProfile[]); });
  }, [addOpen, publisherId]);

  const addMember = async () => {
    if (!selectedUserId) { toast.error('Select a person'); return; }
    setSaving(true);
    try {
      const { error } = await supabase.rpc('add_production_member', {
        p_production_id: productionId,
        p_auth_user_id: selectedUserId,
        p_role_label: roleLabel || undefined,
        p_department: department || undefined,
        p_is_admin: false,
      });
      if (error) throw error;
      toast.success('Member added');
      // Reload members
      const { data } = await supabase
        .from('production_members')
        .select('*')
        .eq('production_id', productionId);
      onMembersChange((data ?? []) as ProductionMember[]);
      setAddOpen(false);
      setSelectedUserId(''); setRoleLabel(''); setDepartment('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to add member');
    } finally {
      setSaving(false);
    }
  };

  // Combine studio + guild, deduplicate
  const allPeople = [
    ...studioMembers.map(m => ({ id: m.auth_user_id, name: m.full_name ?? m.email ?? m.auth_user_id, source: 'studio' })),
    ...guildProfiles
      .filter(g => !studioMembers.some(s => s.auth_user_id === g.user_id))
      .map(g => ({ id: g.user_id, name: g.display_name ?? g.user_id, source: 'guild' })),
  ].filter(p => !members.some(m => m.auth_user_id === p.id));

  const filtered = allPeople.filter(p =>
    !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{members.length} member{members.length !== 1 ? 's' : ''}</span>
        {isManager && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <UserPlus className="w-3.5 h-3.5 mr-1" />Add Member
          </Button>
        )}
      </div>

      <div className="partner-card overflow-hidden divide-y divide-border/40">
        {members.length === 0 && (
          <div className="py-8 text-center text-muted-foreground text-sm">No crew members yet.</div>
        )}
        {members.map(m => (
          <div key={m.id} className="flex items-center gap-3 px-4 py-3">
            <Avatar className="w-8 h-8">
              <AvatarImage src={m.avatar_url ?? undefined} />
              <AvatarFallback className="text-xs">{initials(m.display_name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{m.display_name ?? m.auth_user_id.slice(0, 12)}</p>
              <div className="flex gap-2 mt-0.5">
                {m.department && <span className="text-[10px] font-mono text-muted-foreground uppercase">{m.department}</span>}
                {m.role_label && <span className="text-[10px] text-muted-foreground">{m.role_label}</span>}
              </div>
            </div>
            {m.is_admin && (
              <Badge variant="outline" className="text-[10px]">Admin</Badge>
            )}
          </div>
        ))}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Add Crew Member</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              placeholder="Search by name…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <div className="max-h-48 overflow-y-auto rounded-md border border-border divide-y divide-border/50">
              {filtered.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">No results</p>
              )}
              {filtered.map(p => (
                <button
                  key={p.id}
                  onClick={() => setSelectedUserId(p.id)}
                  className={[
                    'w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent/50 transition-colors',
                    selectedUserId === p.id ? 'bg-primary/10' : '',
                  ].join(' ')}
                >
                  <Avatar className="w-6 h-6">
                    <AvatarFallback className="text-[8px]">{initials(p.name)}</AvatarFallback>
                  </Avatar>
                  <span className="flex-1 truncate">{p.name}</span>
                  <span className="text-[9px] text-muted-foreground font-mono uppercase">{p.source}</span>
                  {selectedUserId === p.id && <Check className="w-3.5 h-3.5 text-primary" />}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Department</Label>
                <Input value={department} onChange={e => setDepartment(e.target.value)} placeholder="e.g. Animation" className="h-7 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Role</Label>
                <Input value={roleLabel} onChange={e => setRoleLabel(e.target.value)} placeholder="e.g. Lead Animator" className="h-7 text-xs" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addMember} disabled={saving || !selectedUserId}>
              {saving ? 'Adding…' : 'Add to Production'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Production Header ──────────────────────────────────────────────────────────

function ProductionHeader({
  production,
  isManager,
  onPhaseChange,
}: {
  production: Production;
  isManager: boolean;
  onPhaseChange: (phase: ProductionPhase) => void;
}) {
  return (
    <div className="flex flex-col gap-1 pb-4 border-b border-border/50 mb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-medium">{production.title}</h1>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
              {production.production_type} · {production.pipeline_template === 'animation' ? 'Animation Pipeline' : 'Live Action Pipeline'}
            </span>
            {production.work_id && (
              <Link to={`/content/movies/${production.work_id}`} className="flex items-center gap-1 text-[10px] text-primary hover:underline">
                <Link2 className="w-2.5 h-2.5" />Linked work
              </Link>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Phase selector */}
          <div className="flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{fmtDate(production.start_date)} → {fmtDate(production.target_release_date)}</span>
          </div>

          {isManager ? (
            <Select value={production.phase} onValueChange={v => onPhaseChange(v as ProductionPhase)}>
              <SelectTrigger className="h-7 text-xs w-auto gap-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PHASE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-xs capitalize px-2 py-1 rounded bg-muted text-muted-foreground">
              {production.phase.replace('_', ' ')}
            </span>
          )}
        </div>
      </div>

      {production.description && (
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{production.description}</p>
      )}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────────

export default function ProductionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { publisher, user, canManageTeam } = useAuth();

  const [production, setProduction] = useState<Production | null>(null);
  const [members, setMembers] = useState<ProductionMember[]>([]);
  const [shots, setShots] = useState<Shot[]>([]);
  const [loading, setLoading] = useState(true);

  const isManager = canManageTeam();

  useEffect(() => {
    if (!id) return;
    Promise.all([
      supabase.from('productions').select('*').eq('id', id).single(),
      supabase.from('production_members').select('*').eq('production_id', id),
      supabase.from('production_shots').select('*').eq('production_id', id).order('sort_order'),
    ]).then(([prodRes, membRes, shotRes]) => {
      if (prodRes.error || !prodRes.data) {
        toast.error('Production not found');
        navigate('/productions');
        return;
      }
      setProduction(prodRes.data as Production);
      setMembers((membRes.data ?? []) as ProductionMember[]);
      setShots((shotRes.data ?? []) as Shot[]);
      setLoading(false);
    });
  }, [id]);

  const updatePhase = async (phase: ProductionPhase) => {
    if (!production) return;
    setProduction(p => p ? { ...p, phase } : p);
    await supabase.from('productions').update({ phase }).eq('id', production.id);
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-6 w-48 bg-muted rounded" />
        <div className="h-4 w-96 bg-muted rounded" />
        <div className="h-64 bg-muted rounded-xl" />
      </div>
    );
  }

  if (!production) return null;

  return (
    <div className="space-y-0">
      {/* Back link */}
      <button onClick={() => navigate('/productions')} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" />
        All Productions
      </button>

      <ProductionHeader
        production={production}
        isManager={isManager}
        onPhaseChange={updatePhase}
      />

      <Tabs defaultValue="board" className="w-full">
        <TabsList className="mb-4 h-8">
          <TabsTrigger value="board"   className="text-xs">Board</TabsTrigger>
          <TabsTrigger value="shots"   className="text-xs">Shots</TabsTrigger>
          <TabsTrigger value="milestones" className="text-xs">Milestones</TabsTrigger>
          <TabsTrigger value="team"    className="text-xs">Team</TabsTrigger>
        </TabsList>

        <TabsContent value="board" className="mt-0">
          <BoardTab
            productionId={production.id}
            members={members}
            shots={shots}
            isManager={isManager}
            currentUserId={user?.id ?? ''}
          />
        </TabsContent>

        <TabsContent value="shots" className="mt-0">
          <ShotsTab
            productionId={production.id}
            pipelineTemplate={production.pipeline_template}
            members={members}
            isManager={isManager}
          />
        </TabsContent>

        <TabsContent value="milestones" className="mt-0">
          <MilestonesTab
            productionId={production.id}
            isManager={isManager}
          />
        </TabsContent>

        <TabsContent value="team" className="mt-0">
          <TeamTab
            productionId={production.id}
            publisherId={publisher?.id ?? ''}
            members={members}
            isManager={isManager}
            onMembersChange={setMembers}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
